<template>
  <div class="player-area-comp">
    <div class="player-area">
      <div class="player-container">
        <template v-if="roomInfo">
          <room-video v-if="roomInfo.state===1" :room-info="roomInfo" :hide-full-screen="true" :hide-switch-quality="true" ref="player">
            <template v-slot:enter-btn>
              <a class="enter-live-room-btn" 
                @click="enterRoom" 
                :href="`${url}${roomInfo.room_id}?fromSpmId=${spmId}`" 
                target="_blank"
              >{{$t('home.clickToLive')}}</a>
            </template>
          </room-video>  
          <room-video-closed v-if="roomInfo.state===0" :room-info="roomInfo"/>
        </template>
        
      </div>
      
      <div class="player-aside">
        <div  
          v-for="(item, index) in playerAreaData" 
          :key="index" 
          class="player-aside-item" 
          :class="{selected: index===selectedIndex}"
          @click="loadPlayer(index)"
          @mouseenter="onAsideMouseEnter"
          @mousemove="onAsideMouseMove"
          @mouseleave="onAsideMouseLeave"
         >
          <div class="player-aside-item-cover">
            <img :src="item.room_info.cover + '@260w_146h_1e_1c_1f.webp'"  class="player-aside-item-cover-img" @error="onCoverImgError"/>
          </div>
          <span class="player-aside-item-title">{{item?.room_info.title}}</span>
          <span class="player-aside-item-title-full" :style="fullTitleStyle">{{item?.room_info.title}}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, unref, toRefs, onMounted, computed, useSSRContext} from 'vue';
import type { IRoomInfoResponseData } from '@/service/room/types';
import type {ICarouselCard} from '@/service/home/types';
import RoomVideo from '@/components/room-video';
import RoomVideoClosed from '@/components/room-video-closed';
import roomService from '@/service/room';
import { initBSocket, closeBSocket } from '@/service/broadcast';
import { EB_EVENT_NAME } from '@/utils/eventbus';
import { useEventBus } from '@/composables/use-eventbus';
import defaultCover from '@/assets/default-cover.png';
import { LogReporter } from '@/common/log-report';

const emitter = useEventBus();

const props = defineProps<{playerAreaData:ICarouselCard[]}>();
const { playerAreaData } = toRefs(props);
const player = ref();

const selectedIndex = ref(0);
let selectedRoomId = 0;
const spmId = LogReporter.getSpmId();
const roomInfo = ref<null|IRoomInfoResponseData>(null);


const loadPlayer = async (index:number):Promise<void> => {
  const prevIdx = selectedIndex.value;
  const prevItem = unref(playerAreaData)[prevIdx];
  closeBSocket();

  const item = unref(playerAreaData)[index];
  selectedIndex.value = index;
  const { room_id } = item?.room_info || {};
  selectedRoomId = room_id;
  try{
    roomInfo.value = null;
    const { data } = await roomService.getRoomInfo(room_id);

    // 未开播时，继续轮播下一个
    // if(data.state === 0 && index < playerAreaData.value.length-1){ 
    //   return loadPlayer(index+1);
    // }

    // 拉取的直播间与当前选中的直播间不同时，不加载直播流
    if(data.room_id !== selectedRoomId){
      return;
    }
    roomInfo.value = data;
    initBSocket(roomInfo.value!.room_id);
  }catch(e){
    console.log('**error(loadPlayer)',e);
  }
};

const url = computed (() => {
  let path;
  if(import.meta.env.SSR){
    const ssrContext = useSSRContext();
    path = ssrContext?.url;
  }else{
    path = window.location.href;
  }
  return path.endsWith('/') ? path : `${path}/`;
});

const enterRoom = () => {
  // const { room_id } = unref(playerAreaData)[selectedIndex.value].room_info;
  // window.open(location.href + (location.href.endsWith('/')? room_id : `/${room_id}`));
  player.value?.pause();
};

onMounted(() => {
  let n = parseInt(Math.random() * 4, 10);
  loadPlayer(n);
});

emitter.on(EB_EVENT_NAME.LIVE_CLOSE, (e) => {
  loadPlayer(selectedIndex.value);
});
emitter.on(EB_EVENT_NAME.LIVE_OPEN, (e) => {
  loadPlayer(selectedIndex.value);
});

if(typeof window !== 'undefined'){
  document.addEventListener('visibilitychange', () => {
    if( document.visibilityState === 'visible'){
      // loadPlayer(selectedIndex.value);
    }
  });
}

const onCoverImgError = async(e) => {
  const src = e.target.getAttribute('src');
  if(!src.includes('default-cover')){
    e.target.setAttribute('src', defaultCover);
  }
};

const fullTitleStyle = ref({
  top: '0px',
  left: '0px',
  visibility: 'hidden'
});

let mouseTimer:number|null = null;

const onAsideMouseEnter = (e:MouseEvent) => {
};

const onAsideMouseMove = (e:MouseEvent) => {
  if(mouseTimer){
    clearTimeout(mouseTimer);
    mouseTimer = null;
  }
  mouseTimer = window.setTimeout(() => {
    fullTitleStyle.value.visibility = 'visible';
    fullTitleStyle.value.top = e.pageY + 20 + 'px';
    fullTitleStyle.value.left = e.pageX + 0 +'px';
  }, 1000);
};

const onAsideMouseLeave =(e:MouseEvent) => {
  if(mouseTimer){
    clearTimeout(mouseTimer);
    mouseTimer = null;
  }
  fullTitleStyle.value.visibility = 'hidden';
};

defineExpose({
  player
});
</script>

<style lang="less" scoped>
@import '@/styles/var.less';
@import '@/styles/util.less';

.player-area-comp {
  background: url('@/assets/home-player-bg.jpg') no-repeat center center;
  background-size: cover;
  background-color: #c9cfff;
  width: 100%;
  padding: 24px 0;

  .player-area {
    margin: 0 auto;
    background: rgba(26, 26, 26, 0.8);
    border-radius: 4px;
    overflow: visible;
    display: flex;

    .player-container {
      position: relative;
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
      overflow: hidden;

      .enter-live-room-btn {
        min-width: 240px;
        padding: 0 12px;
        height: 90px;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        background: rgba(0, 0, 0, 0.6);
        border: 2px solid rgba(76, 147, 255, 1);
        border-radius: 8px;
        font-size: 32px;
        color: #4c93ff;
        cursor: pointer;
        font-weight: 400;
        position: absolute;
        z-index: 1;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);

        &:hover {
          background-color: #4c93ff;
          color: #fff;
        }
      }
    }

    .player-aside {
      padding: 14px 11px 0;

      .player-aside-item {
        position: relative;
        border-radius: 10px;
        font-size: 0;
        cursor: pointer;
        border: 2px solid transparent;

        &:not(:first-of-type) {
          margin-top: 12px;
        }

        &::before {
          content: '';
          position: absolute;
          z-index: 1;
          top: 0;
          left: 0;
          bottom: 0;
          right: 0;
          background: rgba(0, 0, 0, 0.5);
          border-radius: 8px;
        }

        &.selected {
          border: 2px solid rgba(76, 147, 255, 1);

          &::before {
            display: none;
          }

          &::after {
            content: '';
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            left: -6px;
            height: 0;
            width: 0;
            border-right: 6px solid #4c93ff;
            border-top: 6px solid transparent;
            border-bottom: 6px solid transparent;
            transition: opacity cubic-bezier(0.22, 0.58, 0.12, 0.98) 0.4s;
          }
        }

        &:hover {
          border: 2px solid rgba(76, 147, 255, 1);

          &::before {
            display: none;
          }

          .player-aside-item-title-full {
            display: block;
          }
        }

        .player-aside-item-cover {
          position: relative;
          width: 100%;
          height: 100%;
          border-radius: 8px;
          overflow: hidden;
          background: #333;
          display: flex;
          align-items: center;
          justify-content: center;

          &::after {
            content: '';
            position: absolute;
            left: 0;
            right: 0;
            top: 0;
            bottom: 0;
          }

          .player-aside-item-cover-img {
            width: 100%;
            height: 100%;

            &[src*='default-cover'] {
              width: 48px;
              height: 48px;
            }
          }
        }

        .player-aside-item-title {
          position: absolute;
          left: 0;
          bottom: 0;
          font-size: 12px;
          color: #fff;
          line-height: 1;
          font-weight: 700;
          width: 100%;
          background-image: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.3) 57%, rgba(0, 0, 0, 0.5) 99%);
          border-radius: 0 0 8px 8px;
          padding: 12px;
          .text-ellipsis;
        }

        .player-aside-item-title-full {
          display: none;
          position: fixed;
          z-index: 2;
          left: 0;
          top: 0;
          background: rgba(51, 51, 51, 0.8);
          border-radius: 4px;
          font-size: 12px;
          line-height: 1;
          color: #fff;
          letter-spacing: 0;
          font-weight: 700;
          padding: 12px;
          word-break: break-all;
        }
      }
    }
  }

  // >= 2318
  @media (min-width: @screen-xxl) {
    .player-area {
      width: 1436px;
      height: 648px;

      .player-container {
        height: 100%;
        width: 1152px;

        .enter-live-room-btn {
          min-width: 240px;
          height: 90px;
          padding: 0 12px;
          font-size: 32px;
        }
      }

      .player-aside {
        width: 284px;
        height: 100%;

        .player-aside-item {
          width: 260px;
          height: 146px;
        }
      }
    }
  }

  // [1920, 2318)
  @media (max-width: @screen-xxl) and (min-width: @screen-xl) {
    .player-area {
      width: 1275px;
      height: 576px;

      .player-container {
        height: 100%;
        width: 1024px;

        .enter-live-room-btn {
          min-width: 240px;
          height: 90px;
          padding: 0 12px;
          font-size: 32px;
        }
      }

      .player-aside {
        width: 251px;
        height: 100%;

        .player-aside-item {
          width: 227px;
          height: 128px;
        }
      }
    }
  }

  // [1280, 1920)
  @media (max-width: @screen-l-max) and (min-width: @screen-m) {
    .player-area {
      width: 907px;
      height: 410px;

      .player-container {
        height: 100%;
        width: 729px;

        .enter-live-room-btn {
          min-width: 192px;
          height: 72px;
          padding: 0 10px;
          font-size: 26px;
        }
      }

      .player-aside {
        width: 178px;
        height: 100%;

        .player-aside-item {
          width: 154px;
          height: 87px;

          .player-aside-item-cover {
            .player-aside-item-cover-img {
              &[src*='default-cover'] {
                width: 40px;
                height: 40px;
              }
            }
          }
        }
      }
    }
  }

  // [888, 1280)
  @media (max-width: @screen-m) and (min-width: @screen-s) {
    .player-area {
      width: 768px;
      height: 347px;

      .player-container {
        height: 100%;
        width: 617px;

        .enter-live-room-btn {
          min-width: 168px;
          height: 63px;
          padding: 0 8px;
          font-size: 22px;
        }
      }

      .player-aside {
        width: 151px;
        height: 100%;

        .player-aside-item {
          width: 127px;
          height: 72px;

          .player-aside-item-cover {
            .player-aside-item-cover-img {
              &[src*='default-cover'] {
                width: 24px;
                height: 24px;
              }
            }
          }
        }
      }
    }
  }

  // < 888
  @media (max-width: @screen-s) {
    .player-area {
      width: 480px;
      height: 353px;
      display: block;

      .player-container {
        height: 270px;
        width: 100%;

        .enter-live-room-btn {
          min-width: 144px;
          height: 54px;
          padding: 0 7px;
          font-size: 20px;
        }
      }

      .player-aside {
        width: 100%;
        height: 83px;
        display: flex;
        padding: 12px 14px;

        .player-aside-item {
          width: 104px;
          height: 59px;
          margin-top: 0 !important;

          &:not(:first-of-type) {
            margin-left: 12px;
          }

          .player-aside-item-title {
            display: none;
          }

          .player-aside-item-cover {
            .player-aside-item-cover-img {
              &[src*='default-cover'] {
                width: 24px;
                height: 24px;
              }
            }
          }

          &.selected {
            &::after {
              content: '';
              position: absolute;
              top: -14px;
              transform: translateX(-50%);
              left: 50%;
              height: 0;
              width: 0;
              border-right: 6px solid transparent;
              border-left: 6px solid transparent;
              border-bottom: 6px solid #4c93ff;
              transition: opacity cubic-bezier(0.22, 0.58, 0.12, 0.98) 0.4s;
            }
          }
        }
      }
    }
  }
}
</style>